(() => {
  const ELEMENT_NAME = 'luma-product-selector';
  const STORAGE_VERSION = 1;
  const DEFAULT_PACK_READY_EVENT = 'luma:pack-ready';
  const registry = window.LumaCollection || {
    instances: new Map(),
    getState(sectionId) {
      const instance = this.instances.get(sectionId);
      return instance ? instance.getPublicState() : null;
    },
  };
  window.LumaCollection = registry;

  class LumaProductSelector extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized === 'true') return;
      this.dataset.initialized = 'true';

      this.sectionId = this.dataset.sectionId || 'luma';
      this.storageKey = `luma:selector:${this.dataset.collectionHandle || 'collection'}:v${STORAGE_VERSION}`;
      this.isCatalogLoading = false;
      this.isPageLoading = false;
      this.catalogRequestSequence = 0;
      this.catalogController = null;
      this.pageController = null;
      this.pendingCatalogUrl = null;
      this.pendingCatalogPushHistory = false;
      this.failedAction = 'page';
      this.selected = [];
      this.offerQuantity = this.safeQuantity(this.dataset.defaultOffer) || 2;
      this.offerPrice = this.priceForQuantity(this.offerQuantity);
      this.noticeTimer = null;
      this.pendingFlights = new Set();
      this.activeFlights = new Map();

      this.refreshElements();
      this.resetCatalogTracking();
      this.restoreState();

      this.onClick = this.onClick.bind(this);
      this.onChange = this.onChange.bind(this);
      this.onSubmit = this.onSubmit.bind(this);
      this.onToggle = this.onToggle.bind(this);
      this.onPopState = this.onPopState.bind(this);
      this.addEventListener('click', this.onClick);
      this.addEventListener('change', this.onChange);
      this.addEventListener('submit', this.onSubmit);
      this.addEventListener('toggle', this.onToggle, true);
      window.addEventListener('popstate', this.onPopState);

      this.render();
      this.syncFilterExpandedStates();
      this.setupObserver();
      this.setupFooterObserver();
      registry.instances.set(this.sectionId, this);
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('change', this.onChange);
      this.removeEventListener('submit', this.onSubmit);
      this.removeEventListener('toggle', this.onToggle, true);
      window.removeEventListener('popstate', this.onPopState);
      this.observer?.disconnect();
      this.footerObserver?.disconnect();
      this.catalogController?.abort();
      this.pageController?.abort();
      if (registry.instances.get(this.sectionId) === this) {
        registry.instances.delete(this.sectionId);
      }
      if (this.noticeTimer) window.clearTimeout(this.noticeTimer);
      this.activeFlights.forEach((flight) => flight.cancel());
      this.activeFlights.clear();
      this.pendingFlights.clear();
    }

    refreshElements() {
      this.controls = this.querySelector('[data-luma-controls]');
      this.grid = this.querySelector('[data-luma-grid]');
      this.empty = this.querySelector('[data-luma-empty]');
      this.sentinel = this.querySelector('[data-luma-sentinel]');
      this.loader = this.querySelector('[data-luma-loader]');
      this.loadError = this.querySelector('[data-luma-load-error]');
      this.loadEnd = this.querySelector('[data-luma-load-end]');
      this.progressMessage = this.querySelector('[data-luma-progress-message]');
      this.progressCount = this.querySelector('[data-luma-progress-count]');
      this.progressSteps = this.querySelector('[data-luma-progress-steps]');
      this.notice = this.querySelector('[data-luma-notice]');
      this.summaryCount = this.querySelector('[data-luma-summary-count]');
      this.summaryMessage = this.querySelector('[data-luma-summary-message]');
      this.summary = this.querySelector('[data-luma-summary]');
      this.thumbnails = this.querySelector('[data-luma-thumbnails]');
      this.total = this.querySelector('[data-luma-total]');
      this.cta = this.querySelector('[data-luma-cta]');
    }

    resetCatalogTracking() {
      const initialPage = Number(this.grid?.dataset.page || 1);
      this.nextUrl = this.sentinel?.dataset.nextUrl || '';
      this.loadedPages = new Set([
        Number.isFinite(initialPage) && initialPage > 0 ? initialPage : 1,
      ]);
      this.seenProductIds = new Set();
      this.querySelectorAll('[data-luma-card]').forEach((card) => {
        if (card.dataset.productId) {
          this.seenProductIds.add(card.dataset.productId);
        }
      });
    }

    label(name) {
      return this.dataset[name] || '';
    }

    safeQuantity(value) {
      const quantity = Number(value);
      return [1, 2, 3].includes(quantity) ? quantity : null;
    }

    priceForQuantity(quantity) {
      const offer = this.querySelector(
        `[data-luma-offer][data-quantity="${quantity}"]`,
      );
      const price = Number(offer?.dataset.price);
      return Number.isFinite(price) && price > 0 ? price : 0;
    }

    validProduct(value) {
      if (!value || typeof value !== 'object') return null;
      const required = [
        'productId',
        'variantId',
        'title',
        'handle',
        'imageUrl',
        'productUrl',
      ];
      if (required.some((key) => typeof value[key] !== 'string')) return null;
      if (!value.productId || !value.variantId || !value.title || !value.handle) {
        return null;
      }
      return {
        productId: value.productId,
        variantId: value.variantId,
        title: value.title.slice(0, 240),
        handle: value.handle.slice(0, 180),
        imageUrl: value.imageUrl,
        productUrl: value.productUrl,
      };
    }

    restoreState() {
      let stored;
      try {
        stored = JSON.parse(sessionStorage.getItem(this.storageKey) || 'null');
      } catch {
        try {
          sessionStorage.removeItem(this.storageKey);
        } catch {
          // Selection remains functional when sessionStorage is unavailable.
        }
        return;
      }
      if (!stored || stored.version !== STORAGE_VERSION) return;

      const quantity = this.safeQuantity(stored.offerQuantity);
      if (quantity) {
        this.offerQuantity = quantity;
        this.offerPrice = this.priceForQuantity(quantity);
      }

      const unique = new Set();
      const restored = Array.isArray(stored.selectedProducts)
        ? stored.selectedProducts
            .map((item) => this.validProduct(item))
            .filter(
              (item) =>
                item && !unique.has(item.productId) && unique.add(item.productId),
            )
        : [];
      this.selected = restored.slice(0, this.offerQuantity);
    }

    persist() {
      const state = {
        version: STORAGE_VERSION,
        offerQuantity: this.offerQuantity,
        selectedProducts: this.selected,
      };
      try {
        sessionStorage.setItem(this.storageKey, JSON.stringify(state));
      } catch {
        // Selection remains fully functional when sessionStorage is unavailable.
      }
      return state;
    }

    productFromCard(card) {
      return this.validProduct({
        productId: card.dataset.productId || '',
        variantId: card.dataset.variantId || '',
        title: card.dataset.productTitle || '',
        handle: card.dataset.productHandle || '',
        imageUrl: card.dataset.imageUrl || '',
        productUrl: card.dataset.productUrl || '',
      });
    }

    onClick(event) {
      const target = event.target instanceof Element ? event.target : null;
      if (!target) return;

      const offer = target.closest('[data-luma-offer]');
      if (offer && this.contains(offer)) {
        this.changeOffer(this.safeQuantity(offer.dataset.quantity));
        return;
      }

      const selectButton = target.closest('[data-luma-select]');
      if (selectButton && this.contains(selectButton)) {
        const card = selectButton.closest('[data-luma-card]');
        if (card) this.toggleProduct(card);
        return;
      }

      const clearFilter = target.closest('[data-luma-clear-filter]');
      if (clearFilter && this.contains(clearFilter)) {
        const filterParam = clearFilter.dataset.filterParam || '';
        this.querySelectorAll('[data-luma-filter-input]').forEach((input) => {
          if (
            input instanceof HTMLInputElement &&
            (!filterParam || input.name === filterParam)
          ) {
            input.checked = false;
          }
        });
        this.applyCatalogControls();
        return;
      }

      if (target.closest('[data-luma-retry]')) {
        if (this.failedAction === 'catalog' && this.pendingCatalogUrl) {
          this.reloadCatalog(
            this.pendingCatalogUrl,
            this.pendingCatalogPushHistory,
          );
        } else {
          this.loadNextPage();
        }
        return;
      }

      if (target.closest('[data-luma-cta]') && !this.cta?.disabled) {
        this.emitPackReady();
      }
    }

    onChange(event) {
      const target = event.target;
      if (
        target instanceof Element &&
        (target.matches('[data-luma-sort]') ||
          target.matches('[data-luma-filter-input]'))
      ) {
        this.applyCatalogControls();
      }
    }

    onSubmit(event) {
      if (!(event.target instanceof HTMLFormElement)) return;
      if (!event.target.matches('[data-luma-filter-form]')) return;
      event.preventDefault();
      this.applyCatalogControls();
    }

    onToggle(event) {
      if (
        event.target instanceof HTMLDetailsElement &&
        event.target.matches('[data-luma-filter-disclosure]')
      ) {
        this.syncFilterExpandedState(event.target);
      }
    }

    onPopState() {
      this.reloadCatalog(new URL(window.location.href), false);
    }

    syncFilterExpandedState(disclosure) {
      const summary = disclosure.querySelector('summary');
      summary?.setAttribute('aria-expanded', String(disclosure.open));
    }

    syncFilterExpandedStates() {
      this.querySelectorAll('[data-luma-filter-disclosure]').forEach((item) => {
        if (item instanceof HTMLDetailsElement) {
          this.syncFilterExpandedState(item);
        }
      });
    }

    buildCatalogUrl() {
      const url = new URL(window.location.href);
      [...url.searchParams.keys()].forEach((name) => {
        if (
          name === 'page' ||
          name === 'sort_by' ||
          name.startsWith('filter.')
        ) {
          url.searchParams.delete(name);
        }
      });

      const form = this.querySelector('[data-luma-filter-form]');
      if (!(form instanceof HTMLFormElement)) return url;

      const formData = new FormData(form);
      formData.forEach((value, name) => {
        if (typeof value === 'string' && value) {
          url.searchParams.append(name, value);
        }
      });
      return url;
    }

    applyCatalogControls() {
      this.reloadCatalog(this.buildCatalogUrl(), true);
    }

    findIncomingRoot(parsedDocument) {
      return (
        [...parsedDocument.querySelectorAll(ELEMENT_NAME)].find(
          (root) => root.dataset.sectionId === this.sectionId,
        ) || parsedDocument.querySelector(ELEMENT_NAME)
      );
    }

    abortPageRequest() {
      this.pageController?.abort();
      this.pageController = null;
      this.isPageLoading = false;
    }

    async reloadCatalog(url, pushHistory) {
      const storefrontUrl = new URL(url.toString(), window.location.origin);
      storefrontUrl.searchParams.delete('page');
      storefrontUrl.searchParams.delete('section_id');
      const requestUrl = new URL(storefrontUrl);
      requestUrl.searchParams.set('section_id', this.sectionId);

      this.catalogController?.abort();
      this.abortPageRequest();
      this.observer?.disconnect();

      const controller = new AbortController();
      const sequence = this.catalogRequestSequence + 1;
      this.catalogRequestSequence = sequence;
      this.catalogController = controller;
      this.isCatalogLoading = true;
      this.pendingCatalogUrl = storefrontUrl;
      this.pendingCatalogPushHistory = pushHistory;
      this.failedAction = 'catalog';
      this.setLoadState('loading');

      try {
        const response = await fetch(requestUrl.toString(), {
          credentials: 'same-origin',
          headers: { 'X-Requested-With': 'XMLHttpRequest' },
          signal: controller.signal,
        });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);

        const parsedDocument = new DOMParser().parseFromString(
          await response.text(),
          'text/html',
        );
        if (controller.signal.aborted || sequence !== this.catalogRequestSequence) {
          return;
        }

        const incomingRoot = this.findIncomingRoot(parsedDocument);
        const incomingControls = incomingRoot?.querySelector(
          '[data-luma-controls]',
        );
        const incomingGrid = incomingRoot?.querySelector(
          '[data-luma-page-items]',
        );
        const incomingEmpty = incomingRoot?.querySelector('[data-luma-empty]');
        const incomingSentinel = incomingRoot?.querySelector(
          '[data-luma-sentinel]',
        );
        if (
          !incomingControls ||
          !incomingGrid ||
          !incomingEmpty ||
          !incomingSentinel ||
          !this.controls ||
          !this.grid ||
          !this.empty ||
          !this.sentinel
        ) {
          throw new Error('Missing canonical catalog markup');
        }

        this.controls.replaceWith(incomingControls);
        this.grid.replaceWith(incomingGrid);
        this.empty.replaceWith(incomingEmpty);
        this.sentinel.replaceWith(incomingSentinel);
        this.refreshElements();
        this.resetCatalogTracking();

        if (pushHistory) {
          this.persist();
          history.pushState(
            { ...(history.state || {}), lumaCatalog: true },
            '',
            storefrontUrl.toString(),
          );
        }

        this.render();
        this.syncFilterExpandedStates();
        this.pendingCatalogUrl = null;
        if (this.nextUrl) {
          this.setLoadState('idle');
          this.setupObserver();
        } else {
          this.showEndState();
        }
      } catch {
        if (
          controller.signal.aborted ||
          sequence !== this.catalogRequestSequence
        ) {
          return;
        }
        this.showLoadError('catalog');
      } finally {
        if (this.catalogController === controller) {
          this.catalogController = null;
          this.isCatalogLoading = false;
        }
      }
    }

    changeOffer(quantity) {
      if (!quantity || quantity === this.offerQuantity) return;
      const removedCount = Math.max(0, this.selected.length - quantity);
      this.offerQuantity = quantity;
      this.offerPrice = this.priceForQuantity(quantity);
      if (removedCount > 0) {
        const removedProducts = this.selected.slice(quantity);
        removedProducts.forEach((product) => this.cancelFlight(product.productId));
        this.selected = this.selected.slice(0, quantity);
        this.showNotice(this.label('labelOfferTrimmed'));
      }
      this.persist();
      this.render();
    }

    toggleProduct(card) {
      const product = this.productFromCard(card);
      if (!product) return;
      const index = this.selected.findIndex(
        (item) => item.productId === product.productId,
      );

      if (index >= 0) {
        this.cancelFlight(product.productId);
        this.selected.splice(index, 1);
      } else if (this.selected.length >= this.offerQuantity) {
        this.showNotice(this.label('labelSelectionFull'));
        return;
      } else {
        this.pendingFlights.add(product.productId);
        this.selected.push(product);
      }

      this.persist();
      this.render();
      if (index < 0) {
        this.animateProductToSummary(card, product);
      }
    }

    thumbnailForProduct(productId) {
      if (!this.thumbnails) return null;
      return Array.from(this.thumbnails.children).find(
        (thumbnail) => thumbnail.dataset.productId === productId,
      ) || null;
    }

    revealThumbnail(productId, shouldPulse = true) {
      this.pendingFlights.delete(productId);
      const thumbnail = this.thumbnailForProduct(productId);
      if (!thumbnail) return;
      thumbnail.classList.remove('is-awaiting-flight');
      if (!shouldPulse) return;
      thumbnail.classList.remove('is-flight-arrival');
      requestAnimationFrame(() => {
        thumbnail.classList.add('is-flight-arrival');
        window.setTimeout(() => {
          thumbnail.classList.remove('is-flight-arrival');
        }, 260);
      });
    }

    cancelFlight(productId) {
      this.pendingFlights.delete(productId);
      const flight = this.activeFlights.get(productId);
      if (flight) {
        flight.cancel();
        this.activeFlights.delete(productId);
      }
      this.revealThumbnail(productId, false);
    }

    animateProductToSummary(card, product) {
      const destination = this.thumbnailForProduct(product.productId);
      const source = card.querySelector('.luma-card__image');
      const reducedMotion = window.matchMedia(
        '(prefers-reduced-motion: reduce)',
      ).matches;

      if (
        reducedMotion ||
        !(source instanceof HTMLImageElement) ||
        !(destination instanceof HTMLElement) ||
        typeof source.animate !== 'function'
      ) {
        this.revealThumbnail(product.productId, false);
        return;
      }

      const sourceRect = source.getBoundingClientRect();
      const destinationRect = destination.getBoundingClientRect();
      if (
        sourceRect.width <= 0 ||
        sourceRect.height <= 0 ||
        destinationRect.width <= 0 ||
        destinationRect.height <= 0
      ) {
        this.revealThumbnail(product.productId, false);
        return;
      }

      this.cancelFlight(product.productId);
      this.pendingFlights.add(product.productId);
      destination.classList.add('is-awaiting-flight');
      card.classList.add('is-flight-source');
      window.setTimeout(() => card.classList.remove('is-flight-source'), 90);

      const clone = source.cloneNode(false);
      clone.removeAttribute('id');
      clone.removeAttribute('srcset');
      clone.removeAttribute('sizes');
      clone.removeAttribute('loading');
      clone.alt = '';
      clone.setAttribute('aria-hidden', 'true');
      clone.className = 'luma-selection-flight';
      clone.style.setProperty('--luma-flight-top', `${sourceRect.top}px`);
      clone.style.setProperty('--luma-flight-left', `${sourceRect.left}px`);
      clone.style.setProperty('--luma-flight-width', `${sourceRect.width}px`);
      clone.style.setProperty('--luma-flight-height', `${sourceRect.height}px`);
      document.body.append(clone);

      const deltaX =
        destinationRect.left +
        destinationRect.width / 2 -
        (sourceRect.left + sourceRect.width / 2);
      const deltaY =
        destinationRect.top +
        destinationRect.height / 2 -
        (sourceRect.top + sourceRect.height / 2);
      const endScale = Math.min(
        destinationRect.width / sourceRect.width,
        destinationRect.height / sourceRect.height,
        0.34,
      );
      const rotation = deltaX >= 0 ? 5 : -5;
      const animation = clone.animate(
        [
          {
            opacity: 1,
            transform: 'translate3d(0, 0, 0) scale(1) rotate(0deg)',
          },
          {
            opacity: 0.96,
            transform: `translate3d(${deltaX * 0.36}px, ${
              deltaY * 0.24 - 36
            }px, 0) scale(0.82) rotate(${rotation * 0.4}deg)`,
            offset: 0.36,
          },
          {
            opacity: 0.84,
            transform: `translate3d(${deltaX * 0.76}px, ${
              deltaY * 0.69 - 18
            }px, 0) scale(0.52) rotate(${rotation * 0.8}deg)`,
            offset: 0.74,
          },
          {
            opacity: 0.64,
            transform: `translate3d(${deltaX}px, ${deltaY}px, 0) scale(${endScale}) rotate(${rotation}deg)`,
          },
        ],
        {
          duration: 440,
          easing: 'cubic-bezier(0.22, 0.74, 0.22, 1)',
          fill: 'forwards',
        },
      );

      const cleanup = () => {
        animation.cancel();
        clone.remove();
      };
      this.activeFlights.set(product.productId, { cancel: cleanup });
      animation.finished
        .then(() => {
          if (this.activeFlights.get(product.productId)?.cancel !== cleanup) {
            return;
          }
          this.activeFlights.delete(product.productId);
          cleanup();
          this.revealThumbnail(product.productId);
        })
        .catch(() => {
          if (this.activeFlights.get(product.productId)?.cancel === cleanup) {
            this.activeFlights.delete(product.productId);
            cleanup();
            this.revealThumbnail(product.productId, false);
          }
        });
    }

    progressText() {
      const count = this.selected.length;
      if (count >= this.offerQuantity) return this.label('labelProgressReady');
      if (count === 0 && this.offerQuantity === 1) {
        return this.label('labelProgressOne');
      }
      if (count === 0) return this.label('labelProgressFirst');
      const remaining = this.offerQuantity - count;
      return remaining === 1
        ? this.label('labelProgressOneLeft')
        : this.label('labelProgressTwoLeft');
    }

    incompleteText() {
      const remaining = this.offerQuantity - this.selected.length;
      if (remaining <= 0) return this.label('labelSummaryReady');
      if (remaining === 1) return this.label('labelSummaryOneLeft');
      if (remaining === 2) return this.label('labelSummaryTwoLeft');
      return this.label('labelSummaryManyLeft');
    }

    showNotice(message) {
      if (!this.notice || !message) return;
      this.notice.textContent = message;
      this.notice.hidden = false;
      if (this.noticeTimer) window.clearTimeout(this.noticeTimer);
      this.noticeTimer = window.setTimeout(() => {
        this.notice.hidden = true;
        this.notice.textContent = '';
      }, 3600);
    }

    render() {
      const selectedIds = new Map(
        this.selected.map((product, index) => [product.productId, index + 1]),
      );

      this.querySelectorAll('[data-luma-offer]').forEach((offer) => {
        const selected =
          Number(offer.dataset.quantity) === this.offerQuantity;
        offer.classList.toggle('is-selected', selected);
        offer.setAttribute('aria-pressed', String(selected));
      });

      this.querySelectorAll('[data-luma-card]').forEach((card) => {
        const order = selectedIds.get(card.dataset.productId);
        const selected = Boolean(order);
        card.classList.toggle('is-selected', selected);
        const button = card.querySelector('[data-luma-select]');
        const label = card.querySelector('[data-luma-select-label]');
        const badge = card.querySelector('[data-luma-order-badge]');
        button?.setAttribute('aria-pressed', String(selected));
        if (button) {
          const ariaLabel = selected
            ? button.dataset.removeAriaLabel
            : button.dataset.selectAriaLabel;
          if (ariaLabel) button.setAttribute('aria-label', ariaLabel);
        }
        if (label) {
          label.textContent = selected
            ? button?.dataset.selectedLabel || ''
            : button?.dataset.defaultLabel || '';
        }
        if (badge) badge.textContent = order ? String(order) : '';
      });

      const message = this.progressText();
      const countText = `${this.selected.length}/${this.offerQuantity}`;
      if (this.progressMessage) this.progressMessage.textContent = message;
      if (this.progressCount) this.progressCount.textContent = countText;
      if (this.summaryCount) this.summaryCount.textContent = countText;
      if (this.summaryMessage) {
        this.summaryMessage.textContent = this.incompleteText();
      }
      if (this.total) {
        const amount = document.createElement('bdi');
        amount.textContent = `${this.offerPrice} ${this.label('currencyLabel')}`;
        this.total.replaceChildren(amount);
      }

      if (this.progressSteps) {
        this.progressSteps.replaceChildren(
          ...Array.from({ length: this.offerQuantity }, (_, index) => {
            const step = document.createElement('span');
            step.classList.toggle('is-filled', index < this.selected.length);
            return step;
          }),
        );
      }

      if (this.thumbnails) {
        this.thumbnails.replaceChildren(
          ...Array.from({ length: this.offerQuantity }, (_, index) => {
            const product = this.selected[index];
            if (product?.imageUrl) {
              const image = new Image();
              image.src = product.imageUrl;
              image.alt = '';
              image.loading = 'lazy';
              image.width = 68;
              image.height = 84;
              image.dataset.productId = product.productId;
              image.classList.toggle(
                'is-awaiting-flight',
                this.pendingFlights.has(product.productId),
              );
              return image;
            }
            return document.createElement('span');
          }),
        );
      }

      const complete = this.selected.length === this.offerQuantity;
      if (this.cta) {
        this.cta.disabled = !complete;
        this.cta.textContent = complete
          ? this.label('labelCtaReady')
          : this.incompleteText();
      }
    }

    setupObserver() {
      this.observer?.disconnect();
      if (!this.sentinel || !this.nextUrl) {
        this.showEndState();
        return;
      }
      if (!('IntersectionObserver' in window)) {
        this.showLoadError('page');
        return;
      }

      this.observer = new IntersectionObserver(
        (entries) => {
          if (entries.some((entry) => entry.isIntersecting)) {
            this.loadNextPage();
          }
        },
        { rootMargin: '600px 0px', threshold: 0 },
      );
      this.observer.observe(this.sentinel);
    }

    setupFooterObserver() {
      this.footerObserver?.disconnect();
      const footer = document.querySelector('footer, [role="contentinfo"]');
      if (
        !this.summary ||
        !footer ||
        !('IntersectionObserver' in window)
      ) {
        return;
      }

      this.footerObserver = new IntersectionObserver(
        (entries) => {
          const footerVisible = entries.some((entry) => entry.isIntersecting);
          this.summary.classList.toggle('is-footer-visible', footerVisible);
          this.summary.toggleAttribute('inert', footerVisible);
          this.summary.setAttribute('aria-hidden', String(footerVisible));
        },
        { threshold: 0.01 },
      );
      this.footerObserver.observe(footer);
    }

    async loadNextPage() {
      if (
        this.isCatalogLoading ||
        this.isPageLoading ||
        !this.nextUrl
      ) {
        return;
      }
      const requestUrl = new URL(this.nextUrl, window.location.origin);
      const page = Number(requestUrl.searchParams.get('page') || 1);
      if (this.loadedPages.has(page)) {
        this.nextUrl = '';
        this.showEndState();
        return;
      }

      for (const parameter of ['preview_theme_id', 'view', '_fd', 'pb']) {
        const current = new URL(window.location.href).searchParams.get(parameter);
        if (current && !requestUrl.searchParams.has(parameter)) {
          requestUrl.searchParams.set(parameter, current);
        }
      }
      requestUrl.searchParams.set('section_id', this.sectionId);

      const controller = new AbortController();
      this.pageController = controller;
      this.isPageLoading = true;
      this.failedAction = 'page';
      this.setLoadState('loading');

      try {
        const response = await fetch(requestUrl.toString(), {
          credentials: 'same-origin',
          headers: { 'X-Requested-With': 'XMLHttpRequest' },
          signal: controller.signal,
        });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);

        const parsedDocument = new DOMParser().parseFromString(
          await response.text(),
          'text/html',
        );
        if (controller.signal.aborted) return;
        const incomingRoot = this.findIncomingRoot(parsedDocument);
        const incomingGrid = incomingRoot?.querySelector(
          '[data-luma-page-items]',
        );
        if (!incomingGrid) throw new Error('Missing product page markup');

        const fragment = document.createDocumentFragment();
        let added = 0;
        incomingGrid.querySelectorAll('[data-luma-card]').forEach((card) => {
          const productId = card.dataset.productId;
          if (!productId || this.seenProductIds.has(productId)) return;
          this.seenProductIds.add(productId);
          fragment.append(card);
          added += 1;
        });
        this.grid?.append(fragment);
        this.loadedPages.add(page);

        const incomingSentinel = incomingRoot.querySelector(
          '[data-luma-sentinel]',
        );
        const incomingNextUrl = incomingSentinel?.dataset.nextUrl || '';
        if (added === 0 && incomingNextUrl) {
          throw new Error('The next page contained no new products');
        }
        this.nextUrl = incomingNextUrl;
        if (this.sentinel) {
          this.sentinel.dataset.nextUrl = this.nextUrl;
        }

        this.render();
        if (this.nextUrl) {
          this.setLoadState('idle');
        } else {
          this.showEndState();
        }
      } catch {
        if (controller.signal.aborted) return;
        this.showLoadError('page');
      } finally {
        if (this.pageController === controller) {
          this.pageController = null;
          this.isPageLoading = false;
        }
      }
    }

    setLoadState(state) {
      if (this.loader) this.loader.hidden = state !== 'loading';
      if (this.loadError) this.loadError.hidden = state !== 'error';
      if (this.loadEnd) this.loadEnd.hidden = state !== 'end';
    }

    showLoadError(action) {
      this.failedAction = action;
      this.setLoadState('error');
    }

    showEndState() {
      this.nextUrl = '';
      if (this.sentinel) {
        this.sentinel.dataset.nextUrl = '';
      }
      this.observer?.disconnect();
      this.setLoadState('end');
    }

    getPublicState() {
      const selectedProducts = this.selected.map((product) => ({ ...product }));
      return {
        offerQuantity: this.offerQuantity,
        totalPrice: this.offerPrice,
        selectedProducts,
        productIds: selectedProducts.map((product) => product.productId),
        variantIds: selectedProducts.map((product) => product.variantId),
        productTitles: selectedProducts.map((product) => product.title),
        imageUrls: selectedProducts.map((product) => product.imageUrl),
        sourcePageUrl: window.location.href,
      };
    }

    emitPackReady() {
      const eventName =
        this.dataset.packReadyEvent || DEFAULT_PACK_READY_EVENT;
      window.dispatchEvent(
        new CustomEvent(eventName, {
          detail: this.getPublicState(),
        }),
      );
    }
  }

  if (!customElements.get(ELEMENT_NAME)) {
    customElements.define(ELEMENT_NAME, LumaProductSelector);
  }
})();
